"use client";

import { VERTICAL_PRODUCT_TILE_CATEGORY_PATH_IDS } from "@faire/retailer-shared/categories/verticalProductTileCategoryPathIds";
import { useSDUIOnClickHandler } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/ActionHandler/useSDUIOnClickHandler";
import {
  useGenerateBrandUrlForSDUIProductTileHandler,
  useGenerateProductLinkPropsForSDUIProductTileHandler,
} from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/ProductTile/SDUIProductTileUrlHandler";
import { SDUITileInstanceRendererProps } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/types";
import { shouldShowPromotedBadge } from "@faire/retailer-visitor-shared/features/ADS/util";
import { MarketplaceProduct } from "@faire/retailer-visitor-shared/features/MarketplaceItems/MarketplaceProduct/MarketplaceProduct";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import React, { useMemo } from "react";

export const SDUIProductTileWithBrand = ({
  tileElement,
  loggingData,
}: SDUITileInstanceRendererProps) => {
  const { on_click } = tileElement;
  const { product, brand } = tileElement.product_with_brand_info_tile ?? {};
  const brandTile = useMemo(
    () =>
      ILeanProductTile.IBrand.build({
        ...brand,
      }),
    [brand]
  );
  const generateProductLinkProps =
    useGenerateProductLinkPropsForSDUIProductTileHandler(
      product,
      loggingData.view_display_page
    );
  const generateBrandUrl = useGenerateBrandUrlForSDUIProductTileHandler(
    product,
    loggingData.view_display_page
  );

  const brandClickLoggingData = useMemo(() => {
    return {
      ...loggingData,
      action_instance_identifier: brandTile?.token,
    };
  }, [loggingData, brandTile]);

  const onBrandClickHandler = useSDUIOnClickHandler(
    on_click,
    brandClickLoggingData,
    { logSDUIClickEvent: true, logLegacyClickEvent: true }
  );
  const onProductClickHandler = useSDUIOnClickHandler(on_click, loggingData, {
    logSDUIClickEvent: true,
    logLegacyClickEvent: true,
  });

  const showPromotedBadge = shouldShowPromotedBadge(
    product?.product?.is_promoted,
    product?.product?.promoted_by ?? []
  );

  /**
   * This is a temporary solution to show vertical imagery for product tiles
   * in a way that is compatible with RSCs.
   * This should be removed once the BE-driven vertical tile is implemented.
   */
  const showVerticalImagery =
    !!loggingData.view_instance_identifier &&
    VERTICAL_PRODUCT_TILE_CATEGORY_PATH_IDS.includes(
      loggingData.view_instance_identifier
    );

  if (product === undefined || brand === undefined) {
    return null;
  }

  return (
    <MarketplaceProduct
      productTile={product}
      brand={brandTile}
      generateProductLinkProps={generateProductLinkProps}
      generateBrandUrl={generateBrandUrl}
      onClick={(_product, _event) => onProductClickHandler(_event)}
      onBrandClick={(
        _product,
        _brand,
        _isPromoted,
        _promotedBy,
        _impressionId,
        event
      ) => onBrandClickHandler(event)}
      shouldShowPromotedBadge={showPromotedBadge}
      showVerticalImagery={showVerticalImagery}
    />
  );
};
